import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ArrowUpRight, Lightbulb, Clock, BarChart3, Heart, Sparkles, Briefcase } from 'lucide-react';
import flooring from './Images/DINING.webp';
import LivingImage2 from './Images/LIVING.webp';
import fullhouse from './Images/fullbedroom.JPG';
import kitchen4 from './Images/CROCKERY_V1 (1).webp';
import kitchen5 from './Images/CROCKERY_V2.webp' ;
import bedroom4 from './Images/Bedroom 3__.webp';

// Reusable RevealImage Component
const RevealImage = ({ src, alt, className = "h-[350px] md:h-[400px]" }) => {
  const wrapperRef = useRef();
  useEffect(() => {
    const el = wrapperRef.current;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            el.classList.add("revealed");
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.3 }
    );
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);
  
  return (
    <div ref={wrapperRef} className={`reveal-wrapper w-full ${className} relative overflow-hidden`}>
      <img src={src} alt={alt} className="w-full h-full object-cover relative z-1" />
      <div className="reveal-mask"></div>
    </div>
  );
};

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const countRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );
    
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return (
    <span ref={countRef} className="inline-block">
      {count}{suffix}
    </span>
  );
};

// Animated Line Component
const AnimatedLine = () => {
  const lineRef = useRef();
  useEffect(() => {
    const el = lineRef.current;
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            el.classList.add("animate-line");
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.5 }
    );
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, []);
  
  return (
    <div ref={lineRef} className="animated-line-wrapper w-full flex justify-center py-16 md:py-20">
      <div className="animated-line w-32 h-0.5 bg-stone-400"></div>
    </div>
  );
};

const AboutPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});

  // Scroll handler for navbar background
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { 
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const stats = [
    { number: 50, suffix: '+', label: 'HAPPY CLIENTS' },
    { number: 30, suffix: '+', label: 'TEAM MEMBERS' },
    { number: 98, suffix: '%+', label: 'SATISFACTION RATE' },
    { number: 50, suffix: '+', label: 'SUCCESSFUL PROJECTS' }
  ];

  // const benefits = [
  //   {
  //     icon: Lightbulb,
  //     title: 'INNOVATION',
  //     description: 'Adipiscing sollicitudin hendrerit efficitur ex fermentum conubia sed sodales imp sapie dui, parturient ornare lobortis et.'
  //   },
  //   {
  //     icon: Clock,
  //     title: 'FLEXIBLE HOURS',
  //     description: 'Lorem ipsum dolor sit amet consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore magna.'
  //   },
  //   {
  //     icon: BarChart3,
  //     title: 'CAREER GROWTH',
  //     description: 'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.'
  //   },
  //   {
  //     icon: Heart,
  //     title: 'DIVERSITY',
  //     description: 'Lorem ipsum dolor sit amet consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore magna.'
  //   },
  //   {
  //     icon: Sparkles,
  //     title: 'GREAT CULTURE',
  //     description: 'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.'
  //   },
  //   {
  //     icon: Briefcase,
  //     title: 'MEDICAL BENEFITS',
  //     description: 'Adipiscing sollicitudin hendrerit efficitur ex fermentum conubia sed sodales imp sapie dui, parturient ornare.'
  //   }
  // ];
const benefits = [
  {
    icon: Lightbulb,
    title: 'INNOVATIVE DESIGNS',
    description: 'We deliver creative, tailored interior solutions that bring your vision to life with elegance and functionality.'
  },
  {
    icon: Clock,
    title: 'TIMELY DELIVERY',
    description: 'Our projects are completed efficiently without compromising quality, ensuring your space is ready on schedule.'
  },
  {
    icon: BarChart3,
    title: 'PROVEN EXPERTISE',
    description: 'With years of experience in interior design and real estate, we provide professional guidance for every project.'
  },
  {
    icon: Heart,
    title: 'CLIENT-CENTRIC APPROACH',
    description: 'Your needs and preferences guide every decision, ensuring a space that reflects your lifestyle and taste.'
  },
  {
    icon: Sparkles,
    title: 'QUALITY & FINISH',
    description: 'We use premium materials and meticulous craftsmanship to create interiors that last and impress.'
  },
  {
    icon: Briefcase,
    title: 'FULL SERVICE SUPPORT',
    description: 'From consultation to execution, we handle every detail, making your design journey seamless and stress-free.'
  }
];

  return (
    <div className="min-h-screen bg-[#f5f1eb] text-stone-800 overflow-x-hidden">
      {/* Load Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;900&family=Inter:wght@300;400;500;600&display=swap"
        rel="stylesheet"
      />

      {/* Global CSS for smooth scrolling and hidden scrollbars */}
      <style jsx global>{`
        html {
          scroll-behavior: smooth;
        }
        
        /* Hide scrollbars completely */
        ::-webkit-scrollbar {
          width: 0px;
          background: transparent;
        }
        
        body {
          scrollbar-width: none;
          -ms-overflow-style: none;
          overflow-x: hidden;
        }
        
        /* Smooth scroll momentum */
        * {
          -webkit-overflow-scrolling: touch;
        }
      `}</style>

      {/* Enhanced animations */}
      <style jsx>{`
        /* RevealImage Effects */
        .reveal-wrapper {
          position: relative;
          overflow: hidden;
        }
        
        .reveal-wrapper .reveal-mask {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: #f5f1eb;
          transform: translateX(-100%);
          transition: transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          z-index: 2;
        }
        
        .reveal-wrapper.revealed .reveal-mask {
          transform: translateX(100%);
        }
        
        .reveal-wrapper img {
          opacity: 0;
          transform: scale(1.1);
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .reveal-wrapper.revealed img {
          opacity: 1;
          transform: scale(1);
        }
        
        /* Scroll Animations */
        .fade-up {
          opacity: 0;
          transform: translateY(50px);
          transition: all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .fade-up.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .fade-left {
          opacity: 0;
          transform: translateX(-50px);
          transition: all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .fade-left.visible {
          opacity: 1;
          transform: translateX(0);
        }
        
        .fade-right {
          opacity: 0;
          transform: translateX(50px);
          transition: all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .fade-right.visible {
          opacity: 1;
          transform: translateX(0);
        }
        
        .zoom-in {
          opacity: 0;
          transform: scale(0.9);
          transition: all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .zoom-in.visible {
          opacity: 1;
          transform: scale(1);
        }
        
        /* Animated Line */
        .animated-line {
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        
        .animated-line-wrapper.animate-line .animated-line {
          transform: scaleX(1);
        }
        
        .animated-line::before {
          content: '';
          position: absolute;
          top: -1px;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, transparent 0%, #8b7355 50%, transparent 100%);
          transform: translateX(-100%);
          transition: transform 1.5s ease-out;
          border-radius: 2px;
        }
        
        .animated-line-wrapper.animate-line .animated-line::before {
          transform: translateX(100%);
        }
        
        /* Benefit Card Hover Effects */
        .benefit-card {
          transition: all 0.3s ease;
        }
        
        .benefit-card:hover {
          transform: translateY(-5px);
        }
        
        .benefit-card:hover .benefit-icon {
          transform: scale(1.1) rotate(5deg);
        }
        
        .benefit-icon {
          transition: all 0.3s ease;
        }
      `}</style>

      {/* Navigation */}
    
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: LivingImage2 ? `url(${LivingImage2})` : "url('https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1920&h=1080&fit=crop')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        ></div>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-stone-900/40"></div>
        
        {/* Content */}
        <div data-animate id="hero-content">
          <div className={`relative z-10 text-center text-white px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible['hero-content'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light mb-6 md:mb-8 tracking-wider leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              ABOUT US
            </h1>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-stone-800 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div data-animate id="who-we-are-content">
              <div className={`transition-all duration-1000 ${isVisible['who-we-are-content'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                <h2
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light mb-6 md:mb-8 tracking-wider leading-tight"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Who We Are
                </h2>
                <div className="w-16 h-0.5 bg-white mb-8"></div>
                <p className="text-lg md:text-xl leading-relaxed mb-8 text-stone-300">
                 At Construct Insides, we bring you an exclusive portfolio that embodies elegance, innovation, and unparalleled craftsmanship. From the first consultation to project completion, our dedicated team guides you every step of the way—ensuring your vision is perfectly realized and your experience seamless.
                </p>
                <p className="text-base md:text-lg leading-relaxed text-stone-400">
                  From the initial consultation to closing the deal, our dedicated team will guide you through the process.
                </p>
              </div>
            </div>

            {/* Right Images */}
            <div data-animate id="who-we-are-images">
              <div className={`transition-all duration-1000 delay-300 ${isVisible['who-we-are-images'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                <div className="grid grid-cols-2 gap-4 md:gap-6">
                  {/* Top Left - Decorative Object */}
                  <div className="h-[200px] md:h-[250px] lg:h-[300px]">
                    <RevealImage
                      src={kitchen4}
                      alt="Elegant Interior Decor"
                      className="h-full"
                    />
                  </div>
                  
                  {/* Top Right - Living Space */}
                  <div className="h-[250px] md:h-[300px] lg:h-[350px] mt-8 md:mt-12">
                    <RevealImage
                      src={kitchen5}
                      alt="Modern Living Space"
                      className="h-full"
                    />
                  </div>
                  
                  {/* Bottom Full Width - Dining Area */}
                  <div className="col-span-2 h-[200px] md:h-[250px] lg:h-[300px] -mt-8 md:-mt-12">
                    <RevealImage
                      src={bedroom4}
                      alt="Contemporary Dining Area"
                      className="h-full"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-[#f5f1eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left Content */}
            <div data-animate id="stats-content">
              <div className={`transition-all duration-1000 ${isVisible['stats-content'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                <h2
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light mb-6 md:mb-8 tracking-wider leading-tight text-stone-900"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  OUR NUMBERS<br />SPEAK FOR US
                </h2>
                <p className="text-base md:text-lg leading-relaxed text-stone-600 max-w-lg">
               Our achievements and satisfied clients speak volumes about our commitment to excellence and innovative design
                </p>
              </div>
            </div>

            {/* Right Stats Grid */}
            <div data-animate id="stats-grid">
              <div className={`transition-all duration-1000 delay-300 ${isVisible['stats-grid'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                <div className="grid grid-cols-2 gap-8 md:gap-12">
                  {stats.map((stat, index) => (
                    <div key={index} className={`text-center transition-all duration-1000 ${isVisible['stats-grid'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${(index + 1) * 200}ms` }}>
                      <div
                        className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light mb-2 md:mb-4 text-stone-900"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        <AnimatedCounter end={stat.number} suffix={stat.suffix} />
                      </div>
                      <p className="text-xs md:text-sm font-medium tracking-wider text-stone-600 uppercase">
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Line Separator */}
      <AnimatedLine />

      {/* Company Story Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-[#f5f1eb] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-center">
            {/* Left Image */}
            <div data-animate id="story-left-image">
              <div className={`transition-all duration-1000 ${isVisible['story-left-image'] ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                <div className="h-[400px] md:h-[500px] lg:h-[600px]">
                  <RevealImage
                    src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&h=1000&fit=crop"
                    alt="Elegant Living Room"
                    className="h-full"
                  />
                </div>
              </div>
            </div>

            {/* Center Content */}
            <div data-animate id="story-content">
              <div className={`text-center transition-all duration-1000 delay-300 ${isVisible['story-content'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <h2
                  className="text-3xl sm:text-4xl md:text-5xl font-light mb-6 md:mb-8 tracking-wider leading-tight text-stone-900"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  THE STORY BEHIND<br />OUR COMPANY
                </h2>
                <p className="text-base md:text-lg leading-relaxed text-stone-600 mb-6 md:mb-8">
                  Welcome to Construct Insides, where your dream home becomes a reality. Founded by Mr. Harish Kumar Reddy, a seasoned civil engineer with 8 years of experience in interior design and real estate management, our mission is to transform spaces and enrich lives
                </p>
                <p className="text-sm md:text-base leading-relaxed text-stone-600 mb-8 md:mb-12">
                  At Construct Insides, we create interiors that are both beautiful and functional, reflecting the dreams of Hyderabad’s homeowners. Our goal is to make every client feel proud and happy in their space, knowing our designs have added true value. Join our family of satisfied customers and experience the difference with Construct Insides.
                </p>
                <a
                  href="#contact"
                  className="inline-block border-b-2 border-stone-900 text-stone-900 font-medium hover:opacity-70 transition-all duration-300 text-sm md:text-base tracking-[0.15em] uppercase pb-1"
                >
                  CONTACT US
                </a>
              </div>
            </div>

            {/* Right Image */}
            <div data-animate id="story-right-image">
              <div className={`transition-all duration-1000 delay-600 ${isVisible['story-right-image'] ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                <div className="h-[400px] md:h-[500px] lg:h-[600px]">
                  <RevealImage
                    src="https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=800&h=1000&fit=crop"
                    alt="Modern Office Space"
                    className="h-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-[#f5f1eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div data-animate id="benefits-header">
            <div className={`text-center mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible['benefits-header'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h2
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light mb-6 md:mb-8 tracking-wider leading-tight text-stone-900"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                THE BENEFITS OF<br />WORKING WITH US
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-stone-600 max-w-2xl mx-auto">
             
             Experience exceptional design, seamless project management, and personalized solutions tailored to your lifestyle and vision.
              </p>
            </div>
          </div>

          {/* Benefits Grid */}
          <div data-animate id="benefits-grid">
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12 transition-all duration-1000 delay-300 ${isVisible['benefits-grid'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className={`text-center benefit-card transition-all duration-1000 ${isVisible['benefits-grid'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                  style={{ transitionDelay: `${(index + 1) * 100}ms` }}
                >
                  <div className="mb-6 md:mb-8 flex justify-center">
                    <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center">
                      <benefit.icon className="w-8 h-8 md:w-10 md:h-10 text-stone-700 benefit-icon" strokeWidth={1.5} />
                    </div>
                  </div>
                  <h3
                    className="text-lg md:text-xl lg:text-2xl font-light mb-4 md:mb-6 tracking-wider text-stone-900"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {benefit.title}
                  </h3>
                  <p className="text-sm md:text-base leading-relaxed text-stone-600">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 md:py-24 lg:py-32 bg-stone-800 text-white">
        <div data-animate id="cta-section">
          <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${isVisible['cta-section'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light mb-6 md:mb-8 tracking-wider"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              READY TO START YOUR PROJECT?
            </h2>
            <p className="text-lg md:text-xl font-light mb-8 md:mb-12 leading-relaxed opacity-90">
              Let's discuss your vision and create something extraordinary together.
            </p>
            <div data-animate id="cta-buttons">
              <div className={`flex flex-col sm:flex-row gap-6 justify-center items-center transition-all duration-1000 delay-300 ${isVisible['cta-buttons'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <button className="bg-white text-stone-800 px-8 md:px-12 py-3 md:py-4 hover:bg-stone-100 transition-all duration-300 font-light text-sm md:text-base tracking-widest uppercase">
                  GET IN TOUCH
                </button>
                <button className="border border-white text-white px-8 md:px-12 py-3 md:py-4 hover:bg-white hover:text-stone-800 transition-all duration-300 font-light text-sm md:text-base tracking-widest uppercase">
                  VIEW PORTFOLIO
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      
    </div>
  );
};

export default AboutPage;